import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Activity, Eye, UserCheck, Trash2, Edit, FileText, Users, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const actionIcons = {
  devis_create: FileText, devis_send: FileText, devis_cancel: FileText, devis_delete: FileText,
  facture_status: FileText,
  client_create: Users, client_delete: Users,
  role_change: UserCheck, user_delete: Trash2,
  impersonate: Eye,
};

const actionLabels = {
  devis_create: "Devis créé", devis_send: "Devis envoyé", devis_cancel: "Devis annulé", devis_delete: "Devis supprimé",
  facture_status: "Facture mise à jour",
  client_create: "Client créé", client_delete: "Client supprimé",
  role_change: "Rôle modifié", user_delete: "Utilisateur supprimé",
  impersonate: "Impersonation",
};

const actionColors = {
  devis_create: "bg-blue-50 text-blue-600", devis_send: "bg-emerald-50 text-emerald-600",
  devis_cancel: "bg-amber-50 text-amber-600", devis_delete: "bg-red-50 text-red-600",
  facture_status: "bg-purple-50 text-purple-600",
  client_create: "bg-teal-50 text-teal-600", client_delete: "bg-red-50 text-red-600",
  role_change: "bg-orange-50 text-orange-600", user_delete: "bg-red-50 text-red-600",
  impersonate: "bg-violet-50 text-violet-600",
};

export default function WebmasterPage() {
  const { apiFetch, API } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/activity-logs?limit=200`);
      if (res.ok) setLogs(await res.json());
      else toast.error("Erreur de chargement des logs");
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-64" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8" data-testid="webmaster-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">Dashboard WebMaster</h1>
          <p className="text-sm text-slate-500 mt-1">Journal d'activité des administrateurs</p>
        </div>
        <Button onClick={() => { setLoading(true); fetchLogs(); }} variant="outline" className="gap-2" data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total actions", count: logs.length, color: "text-slate-700" },
          { label: "Aujourd'hui", count: logs.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length, color: "text-blue-600" },
          { label: "Cette semaine", count: logs.filter(l => { const d = new Date(l.created_at); const now = new Date(); const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); return d >= weekAgo; }).length, color: "text-emerald-600" },
          { label: "Utilisateurs", count: [...new Set(logs.map(l => l.user_email))].length, color: "text-orange-600" },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className={`text-xl sm:text-2xl font-bold font-barlow ${s.color}`}>{s.count}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Logs table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-600" />
            Activité récente
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">Aucune activité enregistrée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold">Date</TableHead>
                    <TableHead className="text-xs font-semibold">Utilisateur</TableHead>
                    <TableHead className="text-xs font-semibold">Action</TableHead>
                    <TableHead className="text-xs font-semibold hidden sm:table-cell">Cible</TableHead>
                    <TableHead className="text-xs font-semibold hidden md:table-cell">Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => {
                    const Icon = actionIcons[log.action] || Edit;
                    const color = actionColors[log.action] || "bg-slate-100 text-slate-600";
                    return (
                      <TableRow key={log.log_id} data-testid={`log-${log.log_id}`}>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                        <TableCell className="text-xs font-medium text-slate-700">{log.user_email}</TableCell>
                        <TableCell>
                          <Badge className={`${color} text-[10px] border-0 gap-1`}>
                            <Icon className="w-3 h-3" />
                            {actionLabels[log.action] || log.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 hidden sm:table-cell max-w-[150px] truncate">{log.target || "-"}</TableCell>
                        <TableCell className="text-xs text-slate-400 hidden md:table-cell max-w-[200px] truncate">{log.details || "-"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
